import {ChangeDetectionStrategy, Component, inject, signal, Signal, viewChild, WritableSignal} from "@angular/core";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {MessageDetails, MessageLevel} from "../../notification/message/message";
import {NotificationService} from "../../notification/notification.service";
import {MemberSalesData} from '../../identity/member-sales-search.model';
import {EmailFieldComponent} from '../../inputs/email-field/email-field.component';
import {FormComponent} from '../../inputs/form/form.component';
import {InvoiceEmailRequestData, InvoiceEmailRequestService} from "../invoice-email-request.service";
import {EntityData} from "@fedelta-pos/data-client";
import {RecentOrderData} from '../../order/recent-order.service';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {FormsModule} from '@angular/forms';
import {EmailDirective} from '../../inputs/email-field/email.directive';
import {ButtonComponent} from '../../buttons/button/button.component';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {IdentityModel} from '../../identity/identity.model';

@Component({
    selector: "woa-invoice-request-page",
    templateUrl: "./invoice-request-page.component.html",
    providers: [
        InvoiceEmailRequestService,
        {provide: Page, useExisting: InvoiceRequestPageComponent}
    ],
    imports: [
        ButtonComponent,
        EmailDirective,
        EmailFieldComponent,
        FormComponent,
        FormsModule,
        ModalPageLayoutComponent,
        PageFooterControlDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoiceRequestPageComponent extends Page {
    private notificationService: NotificationService = inject(NotificationService);
    private loadingService: LoadingService = inject(LoadingService);
    private invoiceEmailRequestService: InvoiceEmailRequestService = inject(InvoiceEmailRequestService);
    private identityModel: IdentityModel = inject(IdentityModel);

    public readonly form: Signal<FormComponent> = viewChild.required(FormComponent);

    private readonly recentOrder: Signal<RecentOrderData> = navParam<RecentOrderData>("recentOrder");
    private readonly sale: Signal<MemberSalesData> = navParam<MemberSalesData>("sale");

    public readonly sending: WritableSignal<boolean> = signal<boolean>(false);

    public email: string = this.recentOrder() ? this.recentOrder().orderPlacement.email :
        this.identityModel.currentMember() ? this.identityModel.currentMember().contact.email :
            undefined;

    async requestInvoice(): Promise<void> {
        if (!this.form().valid) {
            return;
        }

        this.sending.set(true);
        try {
            await this.loadingService.performWithSpinner(async () => {
                const request: InvoiceEmailRequestData = EntityData.create(InvoiceEmailRequestData);
                request.customerRefNo = this.recentOrder() ? this.recentOrder().placedOrder.lookupCode : this.sale().customerRefNo;
                request.email = this.email;
                await this.invoiceEmailRequestService.performAction(request);
                await this.dismiss(true);
            });
        } catch (error) {
            console.error(error);
            this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.invoice.failed"));
        }
        this.sending.set(false);
    }
}
